import React from 'react';
import { CountryTemplate } from '../../components/CountryTemplate';

export const OmanPage = () => {
  const omanData = {
    name: 'Oman',
    region: 'GCC',
    color: '#A060B0',
    metaTitle: 'Pharma & Nutraceutical Registration in Oman | DGPA&DC | CTD/eCTD Dossiers',
    metaDescription: 'Learn how to register pharmaceutical and nutraceutical products in Oman. DGPA&DC requirements, eCTD-based registration, product classification, GCC food-supplement standards.',
    
    hero: {
      title: 'Pharma & Nutraceutical Registration in Oman',
      subtitle: 'A growing GCC market with high NCD burden, strong import dependence and modern eCTD-based registration under DGPA&DC - ideal for quality generics, biologics and compliant dietary supplements.',
      cta: 'Start Your Oman Registration'
    },
    
    atAGlance: [
      { label: 'Population', value: '~5.28 million' },
      { label: 'Income Level', value: 'Upper-middle to high-income' },
      { label: 'GDP per Capita', value: '~USD 20,000+' },
      { label: 'Health Expenditure', value: '~4.3-4.5% of GDP' },
      { label: 'Pharma Imports', value: '~USD 670-709 million' },
      { label: 'Registration Timeline', value: 'Up to 360 days' },
      { label: 'Registration Fee', value: '500 OMR' }
    ],
    
    marketOverview: {
      title: 'Market Overview: Import-Dependent, GCC-Integrated, Reform-Focused',
      stats: [
        'Oman imports USD 670-709 million of pharmaceutical products annually',
        'Almost all modern medicines are imported finished products',
        'Ministry of Health (MOH) regulates through DGPA&DC',
        'Part of GCC with alignment to GCC guidelines for CTD/eCTD, GxP and pricing',
        'Product classification system distinguishes medicines, herbal medicines, health products, devices',
        'eCTD submission mandatory via MOH digital service'
      ],
      opportunities: [
        'Meaningful pharma import market with quality-focused demand',
        'Modern eCTD submission tools',
        'GCC harmonisation enables dossier reuse across Gulf states',
        'Clear classification guidelines reduce approval uncertainty'
      ]
    },
    
    diseaseburden: {
      title: 'Disease Profile: High Overweight/Obesity and Rising NCDs',
      subtitle: 'Oman faces significant NCD burden driven by lifestyle factors.',
      categories: [
        {
          name: 'Non-Communicable Diseases',
          items: [
            'Over 50% of men and women are overweight or obese',
            'Over 40% have hypertension',
            'About 12% diagnosed with diabetes',
            'Adult obesity: 37.1% women, 26.4% men',
            'Overweight/obesity (BMI 25+): 66% of population',
            'Pharma demand: Antidiabetics, antihypertensives, cardiovascular medicines',
            'Pharma demand: Dyslipidaemia therapies, chronic respiratory treatments'
          ]
        },
        {
          name: 'Nutrition & Lifestyle',
          items: [
            '67% of adults report poor dietary habits',
            'Rising cholesterol levels (~37%)',
            'Diabetes prevalence (~16%)',
            'Nutra potential: Cardiometabolic support, weight-management aids',
            'Nutra potential: Omega-3s, vitamin D, antioxidant formulations',
            'Must meet GCC supplement standards and stay within health product boundaries'
          ]
        }
      ]
    },
    
    regulatoryFramework: {
      authority: 'Ministry of Health - Directorate General of Pharmaceutical Affairs & Drug Control (DGPA&DC)',
      description: 'DGPA&DC is responsible for registration and control of medicines, herbal products, health products and medical devices. Oman uses Guidelines for Product Classification (2018), Biological Products Registration Guideline (2024) with CTD/eCTD expectations, and official Register a Pharmaceutical Product e-service requiring eCTD format.',
      pharma: {
        timeline: 'Official service completion period up to 360 days for pharmaceutical products. Well-prepared eCTD dossiers can be approved in several months. Complex products or query cycles may extend timelines. Registration fee: 500 OMR.',
        requirements: [
          'Marketing Authorisation Holder (MAH) must be recognised by MOH',
          'Foreign manufacturers work via licensed Omani pharmaceutical warehouse/agent',
          'Product classification as pharmaceutical medicine, herbal medicine, health product or medical device',
          'Classification based on claims, composition, dosage, level of action',
          'Mandatory CTD format with eCTD submission via MOH digital service',
          'Complete eCTD dossier (Modules 1-5)',
          'GMP certificate for manufacturing site',
          'CPP / Free Sale Certificate from reference authorities',
          'Labelling, leaflets, artworks (Arabic/English)',
          'COAs and sample packs',
          'Pricing data and proposed prices'
        ],
        insight: 'Oman requires eCTD format with 360-day nominal period. Well-prepared dossiers processed faster. Classification step critical - product classified based on claims, composition, and action level. GCC harmonisation enables CTD reuse across Gulf. Registration valid typically 5 years with eCTD sequence renewals.'
      },
      nutra: {
        timeline: 'Faster than pharmaceutical route if correctly classified as health product/dietary supplement. Must meet GCC GSO 2571:2021 standards.',
        requirements: [
          'DGPA&DC defines dietary supplements as concentrated nutrient sources with nutritional/physiological effect',
          'Must meet minimum and maximum daily dose ranges for vitamins/minerals',
          'Acceptable forms: capsules, tablets, powders, sachets, ampoules, drops',
          'Specific labelling: not substitute for varied diet; keep out of reach of children',
          'GCC Layer: GSO 2571:2021 (Food Supplements) and GSO 654:2021',
          'Application through MOH Drug Safety Center',
          'Pharmaceutical warehouse licence and commercial agency certificate',
          'Full composition (per unit and daily dose)',
          'COA for finished product and active ingredients',
          'Label with mandatory statements, no unauthorized therapeutic claims',
          'High medical claims or doses above maximums = pharmaceutical classification'
        ],
        insight: 'Classification critical - DGPA&DC uses Product Classification Guideline. Dietary supplements need mild-moderate wellness claims and nutrients within GCC limits. High claims or doses trigger full pharmaceutical registration. GCC membership means GSO 2571 and 654 apply for food supplements.'
      }
    },
    
    pharmaPathway: {
      title: 'Pharmaceutical Registration with DGPA&DC',
      steps: [
        'Local Agent / MAH - MAH must be recognised by MOH. Foreign manufacturers via licensed Omani warehouse/agent',
        'Classification Step - Product classified as pharmaceutical medicine, herbal medicine, health product or device',
        'Classification based on: claims, composition, dosage, level of action, reference markets',
        'Dossier Format - CTD/eCTD mandatory via MOH digital service',
        'Module 1: Omani administrative info, MAH, agent agreements',
        'Module 2: Quality & clinical overviews/summaries',
        'Module 3: Quality/CMC data, specs, stability',
        'Modules 4-5: Non-clinical and clinical data for new/biologic products',
        'GMP certificate for manufacturing site',
        'CPP or Free Sale Certificate from reference agency',
        'Labeling, PIL, packaging samples (Arabic/English)',
        'Pricing information and supporting evidence',
        'Service completion: up to 360 days nominal',
        'Well-prepared dossiers: several months in practice',
        'Renewal: Typically every 5 years',
        'Variations: Filed as eCTD sequences'
      ]
    },
    
    nutraPathway: {
      title: 'Nutraceuticals, Dietary Supplements & Health Products',
      steps: [
        'Product Classification Critical - DGPA&DC Product Classification Guideline defines dietary supplements',
        'Concentrated sources of nutrients/substances with nutritional/physiological effect',
        'Must meet minimum/maximum daily dose ranges for vitamins/minerals',
        'Acceptable forms: capsules, tablets, powders, sachets, ampoules, drops',
        'Mandatory labelling: not substitute for varied diet; keep away from children',
        'GCC Standards: GSO 2571:2021 (Food Supplements), GSO 654:2021 (Special dietary foods)',
        'Application for health products registration through MOH Drug Safety Center',
        'Pharmaceutical warehouse licence and commercial agency certificate copy',
        'Full qualitative/quantitative composition (per unit and daily dose)',
        'COA for finished product and active ingredients where necessary',
        'Label and artwork with mandatory statements',
        'No unauthorized therapeutic disease claims',
        'Proof of regulatory status in country of origin',
        'High medical claims or nutrient doses above maximums = pharmaceutical medicine classification'
      ]
    },
    
    requiredDocs: {
      'Pharmaceuticals (eCTD to DGPA&DC)': [
        'Register a Pharmaceutical Product application (e-service)',
        'Full eCTD dossier (Modules 1-5)',
        'Company and manufacturing-site licences',
        'GMP certificate',
        'CPP / Free Sale Certificate',
        'Labelling, leaflets, artworks (Arabic/English)',
        'COAs and sample packs',
        'Pricing data and proposed prices'
      ],
      'Dietary Supplements / Health Products': [
        'Application for health products registration',
        'Pharmaceutical warehouse licence and commercial agency certificate',
        'Full composition (per unit and daily dose)',
        'COA for finished product and active ingredients',
        'Label and artwork with mandatory statements',
        'Proof of regulatory status in country of origin'
      ]
    },
    
    whyThisCountry: {
      title: 'Why Oman Belongs in Your GCC Expansion Plan',
      reasons: [
        'Meaningful pharma import market: USD 670-709 million annually',
        'High NCD burden: over 50% overweight/obese, 12% diabetes, 40% hypertension',
        'Modern regulatory tools: mandatory eCTD submission',
        'GCC harmonisation: enables portfolio and CTD reuse across Saudi, UAE, Qatar, Kuwait, Bahrain',
        'Clear classification guidelines bring predictability',
        'Upper-middle to high-income profile with strong purchasing power'
      ],
      bestPortfolio: [
        'Pharma: Antidiabetics, antihypertensives, statins, antiplatelets, respiratory therapies, women health, metabolic-syndrome treatments, biologics and biosimilars with strong CTD/eCTD packages',
        'Nutra: Heart & metabolic health (omega-3s, plant sterols, CoQ10 within allowed dosage), vitamin D, multivitamins, bone/joint formulas, weight-control supplements per GSO standards'
      ]
    },
    
    howWeHelp: {
      title: 'How TheApostrophe Helps You Enter Oman',
      services: [
        'Portfolio & Country Fit Mapping - Identify SKUs fitting Oman NCD profile and GCC opportunities',
        'Product Classification Strategy - Use DGPA&DC guidelines to pre-empt classification disputes',
        'CTD/eCTD Dossier Adaptation - Convert to Oman-ready eCTD, strengthen Module 3 for biologics',
        'Dietary Supplement Compliance - Calibrate formulation and claims to Oman + GCC limits (GSO 2571/654)',
        'Lifecycle & GCC Cluster Planning - Build reusable plans for wider GCC market'
      ]
    },
    
    faqs: [
      {
        question: 'Who regulates medicines in Oman?',
        answer: 'Medicines are regulated by the Ministry of Health Directorate General of Pharmaceutical Affairs & Drug Control (DGPA&DC), including classification and registration of pharmaceutical medicines, herbal medicines, health products and medical devices.'
      },
      {
        question: 'Does Oman require CTD or eCTD?',
        answer: 'Yes. Oman follows the CTD structure and now requires eCTD format for pharmaceutical registration submissions, as confirmed by the official Gov.om service and DGPA&DC guidelines.'
      },
      {
        question: 'How long does registration take?',
        answer: 'The official service indicates up to 360 days for pharmaceutical products. Well-prepared eCTD dossiers can be approved in several months, while complex products or query cycles may extend timelines.'
      },
      {
        question: 'How are dietary supplements regulated?',
        answer: 'Dietary supplements are classified as health products under DGPA&DC guidelines and must comply with GCC GSO 2571:2021 food-supplement standard. High claims or doses can reclassify a product as medicine requiring full pharmaceutical registration.'
      },
      {
        question: 'Can Indian formulations be registered in Oman?',
        answer: 'Yes, provided they meet WHO-GMP requirements, have robust CTD/eCTD dossiers and are supported by CPP/Free Sale Certificates. Indian pharma exports to Oman reached around USD 12.86 million in 2018, indicating an established trade channel.'
      }
    ]
  };

  return <CountryTemplate countryData={omanData} />;
};
